import React from 'react';
import heroImage from '../assets/article-cardiovascular-risk.png';
import articleBodyImage from '../assets/article-cardiovascular1.png';
import articleClinicalImage from '../assets/article-cardiovascular2.png';

const ArticleCardiovascularRiskCalculators = () => {
  return (
    <div className="bg-white pb-16">
      <section
        className="relative isolate min-h-[38vh] w-full overflow-hidden sm:min-h-[40vh]"
        aria-labelledby="cv-article-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/35 to-slate-950/60"
          aria-hidden
        />
        <div className="relative z-10 mx-auto flex min-h-[38vh] max-w-6xl flex-col items-center justify-center px-4 py-12 sm:min-h-[40vh] sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-3xl text-center">
            <h1
              id="cv-article-hero-heading"
              className="text-pretty text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Cardiovascular risk calculators: limits and context
            </h1>
            <p className="mt-4 text-sm text-slate-100/95 sm:text-base">
              <span className="text-slate-100">By Dr. Rohan Mehta</span>
              <span className="mx-2 text-slate-300/90" aria-hidden>
                ·
              </span>
              <time dateTime="2026-04-15" className="tabular-nums text-slate-100/90">
                April 15, 2026
              </time>
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="cv-article-body-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleBodyImage}
                alt="Modern operating theatre with surgical table, overhead lights, and monitors, as context for cardiovascular care"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">What scores can show</p>
              <h2
                id="cv-article-body-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                What risk calculators do (and do not)
              </h2>
            </div>
            <p>
              Risk scores translate a handful of inputs into a probability or risk category. They can be useful for
              framing prevention—but they simplify complex biology and social determinants of health.
            </p>
            <p>
              We compare what common calculators were built to predict, how populations differ from the cohorts used to
              derive them, and why a “low risk” label never removes the need for clinical judgment.
            </p>
            <p>
              Many tools report short-horizon risk (for example, ten-year fatal or non-fatal events) while treatment
              decisions also weigh lifetime burden, competing risks, and quality of life. If the printout does not say
              exactly which outcome was modelled, ask—otherwise it is easy to compare apples with pears across apps.
            </p>
            <p>
              Equations can be miscalibrated when applied to people who were under-represented in the original studies,
              and they may omit variables your clinician considers essential, from detailed family history to kidney
              function and prior tests. A calculator can prompt a conversation; it should not replace examination,
              trends over time, or shared decision-making about whether prevention fits your goals.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="cv-article-practice-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Visits that inform risk</p>
              <h2
                id="cv-article-practice-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Using a score well in real visits
              </h2>
            </div>
            <p>
              Bring averages, not single spikes: home blood pressure logs with cuff size and arm used, or repeated clinic
              readings on different days, usually matter more than one white-coat number for both risk tools and
              treatment thresholds.
            </p>
            <p>
              Lipids after acute illness or major weight change can mislead; note whether you were fasting, on your usual
              medicines, and how long it has been since pregnancy or a new diagnosis such as diabetes. Small data
              errors propagate straight into the printed risk band.
            </p>
            <p>
              If you land near a guideline boundary, treat it as a prompt to recheck and discuss trade-offs—not as a
              verdict. Benefits, side effects, cost, and your own priorities still determine whether a preventive therapy
              is worth starting or continuing.
            </p>
          </div>
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleClinicalImage}
                alt="Clinical supplies on a reflective counter, including a jar of cotton balls and a rack of small colour-coded syringes"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleCardiovascularRiskCalculators;
